import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AdminService, SettingsData } from '../../services/admin.service';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.css']
})
export class SettingsComponent implements OnInit {
  settingsForm: FormGroup;
  isLoading = false;
  isSaving = false;
  isArchiving = false;
  error: string | null = null;
  success: string | null = null;
  currentSettings: SettingsData | null = null;
  archiveResult: any = null;

  constructor(
    private fb: FormBuilder,
    public authService: AuthService,
    private adminService: AdminService
  ) {
    this.settingsForm = this.fb.group({
      retentionPeriodDays: [365, [Validators.required, Validators.min(1), Validators.max(3650)]]
    });
  }

  ngOnInit(): void {
    if (this.authService.isAdmin()) {
      this.loadSettings();
    }
  }

  loadSettings(): void {
    this.isLoading = true;
    this.error = null;
    this.success = null;

    this.adminService.getSettings().subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          this.currentSettings = {
            retentionPeriodDays: response.data.retentionPeriodDays,
            lastUpdated: response.data.lastUpdated
          };
          this.settingsForm.patchValue({
            retentionPeriodDays: response.data.retentionPeriodDays || 365
          });
        } else {
          this.error = 'Failed to load settings';
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading settings:', error);
        this.error = 'Failed to load settings. Please try again.';
        this.isLoading = false;
      }
    });
  }

  saveSettings(): void {
    if (this.settingsForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.isSaving = true;
    this.error = null;
    this.success = null;

    const retentionPeriodDays = this.settingsForm.value.retentionPeriodDays;

    this.adminService.updateSettings(retentionPeriodDays).subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          this.currentSettings = {
            retentionPeriodDays: response.data.retentionPeriodDays,
            lastUpdated: response.data.lastUpdated
          };
          this.success = 'Settings saved successfully!';
          setTimeout(() => {
            this.success = null;
          }, 3000);
        } else {
          this.error = response.message || 'Failed to save settings';
        }
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error saving settings:', error);
        this.error = error.error?.message || 'Failed to save settings. Please try again.';
        this.isSaving = false;
      }
    });
  }

  archivePosts(): void {
    if (!confirm('Are you sure you want to archive posts older than the retention period? This action cannot be undone.')) {
      return;
    }

    this.isArchiving = true;
    this.error = null;
    this.success = null;
    this.archiveResult = null;

    this.adminService.archivePosts().subscribe({
      next: (response: any) => {
        if (response.success && response.data) {
          this.archiveResult = response.data;
          this.success = response.message || `Successfully archived ${response.data.archivedCount} post(s)!`;
          setTimeout(() => {
            this.success = null;
          }, 5000);
        } else {
          this.error = response.message || 'Failed to archive posts';
        }
        this.isArchiving = false;
      },
      error: (error) => {
        console.error('Error archiving posts:', error);
        this.error = error.error?.message || 'Failed to archive posts. Please try again.';
        this.isArchiving = false;
      }
    });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.settingsForm.controls).forEach(key => {
      this.settingsForm.get(key)?.markAsTouched();
    });
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}

